import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useDropdownPosition } from './useDropdownPosition.js'
import { isMobile } from '@/service/utility'

/**
 * Composable for managing tooltips with Teleport
 * Handles positioning, visibility, and viewport events
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.position - Position: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {number} options.gap - Gap between trigger and tooltip (default: 8)
 * @param {number} options.viewportPadding - Padding from viewport edges (default: 8)
 * @param {string} options.zIndex - Z-index value (default: '50')
 * @param {boolean} options.disabledOnMobile - Disable tooltip on mobile devices (default: true)
 * @returns {Object} Tooltip utilities
 */
export function useTeleportTooltip(options = {}) {
  const {
    position = 'top',
    gap = 8,
    viewportPadding = 8,
    zIndex = '50',
    disabledOnMobile = true
  } = options

  const visible = ref(false)
  const tooltipTrigger = ref(null)
  const tooltipElement = ref(null)

  const { dropdownStyle, calculatePosition } = useDropdownPosition({
    position,
    gap,
    viewportPadding
  })

  // Calculate position when tooltip becomes visible
  const updatePosition = async () => {
    if (visible.value && tooltipTrigger.value && tooltipElement.value) {
      await calculatePosition(tooltipTrigger.value, tooltipElement.value)
    }
  }

  // Handle window resize and scroll
  const handleViewportChange = () => {
    if (visible.value) {
      updatePosition()
    }
  }

  let autoDismissTimer = null;

  const isTouchDevice = () => {
    if (typeof window === 'undefined') return false;
    if (disabledOnMobile && isMobile()) return true;
    const hasCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const noHover = window.matchMedia && window.matchMedia('(hover: none)').matches;
    const hasTouchPoints = (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) || ('ontouchstart' in window);
    return Boolean(hasCoarsePointer || noHover || hasTouchPoints);
  };

  // Show tooltip
  const show = async () => {
    if (isTouchDevice()) {
      visible.value = false;
      return;
    }
    visible.value = true;
    await updatePosition();

    if (autoDismissTimer) clearTimeout(autoDismissTimer);
    autoDismissTimer = setTimeout(() => {
      hide();
    }, 2000);
  }

  // Hide tooltip
  const hide = () => {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer);
      autoDismissTimer = null;
    }
    visible.value = false;
  }

  // Toggle tooltip
  const toggle = async () => {
    if (visible.value) {
      hide();
    } else {
      await show();
    }
  }

  const handleGlobalPointerDown = () => {
    if (visible.value) {
      hide();
    }
  };

  // Watch for changes in visibility and element availability
  watch([visible, tooltipElement], () => {
    if (visible.value && tooltipElement.value) {
      nextTick(() => {
        updatePosition();
      });
    }
  })

  // Setup viewport event listeners
  onMounted(() => {
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('pointerdown', handleGlobalPointerDown, true);
  })

  onUnmounted(() => {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer);
      autoDismissTimer = null;
    }
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    window.removeEventListener('pointerdown', handleGlobalPointerDown, true);
  })

  // Computed style with z-index
  const computedStyle = computed(() => ({
    ...dropdownStyle.value,
    zIndex: typeof zIndex === 'object' && zIndex !== null && 'value' in zIndex ? zIndex.value : zIndex
  }))

  return {
    visible,
    tooltipTrigger,
    tooltipElement,
    dropdownStyle: computedStyle,
    show,
    hide,
    toggle,
    updatePosition
  }
}

